import React, { useRef } from 'react';
import ReactDOM from 'react-dom';
import { PerspectiveCamera } from 'three';
import { Position } from '@photo-sphere-viewer/core';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import DropdownMenu from '@/components/common/Dropdown/DropdownMenu';
import { useDropdownEvents } from '@/components/common/Dropdown';
import { slideObjectPropTypes } from '@/components/admin/SlideShow/Utils/index';
import useSlideShowContext from '@/components/admin/SlideShow/stores/useSlideShowContext';
import useFlags from '@/hooks/useFlags';
import RemoveSlideObject from './RemoveSlideObject';
import ChangeImagePosition from './ChangeImagePosition';
import ChangeImageObjectFit from './ChangeImageObjectFit';
import EditSlideObject from './EditSlideObject';
import EditBackgroundPopover from './EditBackgroundPopover';
import styles from './ContentActions.module.scss';
import EditPaddingPopover from './EditPaddingPopover';
import usePopover from '../hooks/usePopover';
import EditBorderPopover from './EditBorderPopover';
import CameraPositionButton from './CameraPositionButton';
import PanoramicDefaultViewButton from './PanoramicDefaultViewButton';

interface ContentActionsProps {
  isEditable?: boolean;
  modalManager: ModalManager,
  slideObject: slideObjectPropTypes,
  styleDivRef: React.MutableRefObject<HTMLDivElement>,
  threeJsCamera?: PerspectiveCamera;
  threeJsControls?: OrbitControls;
  panoramicCamera: Position;
  thumbnail: boolean;
}

const EDITABLE_TYPES = [
  'HTML', 'Image', 'Video', 'Question', '3DModel', 'PanoramicImage', 'InteractiveMap', 'Section', 'GlossaryTermCard',
  'GlossaryTermList', 'GlossaryTermTable', 'ReadingSection',
];

const ContentActions = ({
  modalManager, slideObject, styleDivRef, threeJsCamera, thumbnail, threeJsControls, panoramicCamera,
}: ContentActionsProps) => {
  const ref = useRef();
  const dropdownEvents = useDropdownEvents(ref);
  const anchorId = `color-popover-anchor-${slideObject.id}`;
  const openerClass = `actions-${slideObject.id}`;
  const { coordinates } = usePopover({ anchorId, dropdownEvents, openerClass, ref, position: 'bottom left' });
  const isSysAdmin = useSlideShowContext(state => state.isSysAdmin);
  const { flushImageTreatment } = useFlags();

  // don't show the edit button for editable types
  // don't show the edit button to teachers for HTML or question objects
  const showEditButton = () => {
    if (!EDITABLE_TYPES.includes(slideObject.modelType)) return false;
    if (isSysAdmin) return true;
    if (slideObject.modelType === 'HTML') return false;
    if (slideObject.modelType === '3DModel') return false;
    if (slideObject.modelType === 'Video' && slideObject.modelVideo) return false;
    if (slideObject.modelType === 'InteractiveMap') return false;
    if (slideObject.modelType === 'PanoramicImage') return false;

    return true;
  };

  return (
    <>
      <div className={styles.actionsButtonWrapper} id={anchorId}>
        <button
          className={`btn btn--outline-purple ${styles.actionsButton} ${openerClass}`}
          type="button"
        >
          <i className={`fa fa-edit ${styles.buttonIcon}`} />
        </button>
      </div>

      {ReactDOM.createPortal(
        <DropdownMenu
          ref={ref}
          className={`${styles.actionsButton}`}
          coordinates={coordinates}
          open={dropdownEvents.open}
        >
          {showEditButton() && (
            <EditSlideObject dropdownEvents={dropdownEvents} modalManager={modalManager} />
          )}
          {showEditButton() && threeJsCamera && slideObject.threeDModels?.length > 0 && (
            <CameraPositionButton
              camera={threeJsCamera}
              controls={threeJsControls}
              modalManager={modalManager}
              slideObject={slideObject}
            />
          )}
          {showEditButton() && slideObject.modelType === 'PanoramicImage' && (
            <PanoramicDefaultViewButton
              panoramicCamera={panoramicCamera}
              slideObjectId={slideObject.id}
              onSuccess={() => dropdownEvents.setOpen(false)}
            />
          )}
          {slideObject.modelType !== 'Question' && slideObject.modelType !== '3DModel' && (
            <>
              <EditBackgroundPopover
                anchorId={`color-popover-anchor-${slideObject.id}`}
                dropdownEvents={dropdownEvents}
                slideObject={slideObject}
                styleDivRef={styleDivRef}
              />
              <EditPaddingPopover
                anchorId={`color-popover-anchor-${slideObject.id}`}
                dropdownEvents={dropdownEvents}
                slideObject={slideObject}
                styleDivRef={styleDivRef}
              />
            </>
          )}
          {slideObject.modelType !== '3DModel' && (
            <EditBorderPopover
              anchorId={`color-popover-anchor-${slideObject.id}${thumbnail ? '-thumbnail' : ''}`}
              dropdownEvents={dropdownEvents}
              slideObject={slideObject}
              styleDivRef={styleDivRef}
            />
          )}
          {flushImageTreatment && slideObject.modelType === 'Image' && (
            <>
              <ChangeImageObjectFit slideObject={slideObject} styleDivRef={styleDivRef} />
              <ChangeImagePosition slideObject={slideObject} styleDivRef={styleDivRef} />
            </>
          )}
          <RemoveSlideObject
            dropdownEvents={dropdownEvents}
            slideObject={slideObject}
          />
        </DropdownMenu>,
        document.body,
      )}
    </>
  );
};

export default ContentActions;
